import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, useTheme } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { byggApiRoutes } from '@klp/bygg/api';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { Block, Heading } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme';
import { ErrorNotification } from '@klp/errorNotification';
import { FileDownload } from '@klp/filedownload';
import { isPdf } from '@klp/helpers';
import { Search, filterSearchResult } from '@klp/search';

import type { ByggDokument } from '../classes';
import { groupDocuments } from '../helpers/groupDocuments';
import { useGetByggDokumentListQuery } from '../rtkApi';

const styles = (theme: Theme) => ({
    download: {
        textAlign: 'center',
        '& > *': {
            display: 'inline-block',
            '& > *': {
                margin: theme.spacing(1),
            },
        },
    },
    previewDownload: {
        cursor: 'pointer',
    },
    scrollTo: {
        display: 'block',
        position: 'relative',
        width: 0,
        height: 0,
    },
});

export interface ByggDokumentListProps {
    byggNummer: string;
}

export const ByggDokumentList: React.FC<ByggDokumentListProps> = ({
    byggNummer,
}) => {
    const classes = useKlpStyles({}, styles);
    const location = useLocation();
    const theme = useTheme();

    const {
        data: responseData,
        isLoading: loading,
        error,
    } = useGetByggDokumentListQuery({ byggNummer }, { skip: !byggNummer });

    const byggDokumentList = useMemo(
        () => responseData?.byggDokumentList || [],
        [responseData?.byggDokumentList],
    );

    // Local state
    const [search, setSearch] = useState('');

    // Handle scroll to hash on data change
    useEffect(() => {
        const hash = location.hash;
        if (!hash) return;

        const element = document.getElementById(
            decodeURI(hash).replace('#', ''),
        );
        if (!element) return;

        const y =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            theme.topbar.height;

        window.scrollTo({ top: y, behavior: 'smooth' });
    }, [byggDokumentList, location.hash, theme.topbar.height]);

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const renderDocumentPreviewDownload = <DataType, FilterType>(
        value: string,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: ByggDokument,
    ): JSX.Element => {
        const { bucketFilnavn } = row;
        return (
            <div className={classes.download}>
                {bucketFilnavn && isPdf(bucketFilnavn) && (
                    <FileDownload
                        path={byggApiRoutes.tegning(value)}
                        name={bucketFilnavn}
                        preview={true}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.SEARCH}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                )}

                {bucketFilnavn && (
                    <FileDownload
                        path={byggApiRoutes.tegning(value)}
                        name={bucketFilnavn}
                    >
                        <Illustration
                            className={classes.previewDownload}
                            icon={IconName.DOWNLOAD}
                            klpPalette="primary"
                            klpColor="main"
                        />
                    </FileDownload>
                )}
            </div>
        );
    };

    const getTranslations = (value: string) => {
        switch (value) {
            case 'Generell informasjon':
                return 'byggDokument.tableTitle.generalInfo';
            case 'Kontraktsvedlegg':
                return 'byggDokument.tableTitle.contractAttachment';
            case 'Brann':
                return 'byggDokument.tableTitle.fire';
            case 'Rømning':
                return 'byggDokument.tableTitle.escape';
            default:
                return '';
        }
    };

    const byggDokumentListGrouped = groupDocuments(
        filterSearchResult<ByggDokument>(byggDokumentList, search, [
            'navn',
            'nummer',
        ]),
    );

    const documentTypes = Object.keys(byggDokumentListGrouped);

    return (
        <Loader isLoading={loading} data-testid="ByggDokumentList">
            <Grid container={true}>
                <Grid item={true} xs={12} sm={6} md={4}>
                    <Block marginTop="compact">
                        <Search
                            id="ByggDokumentListSearch"
                            searchHandler={handleSearch}
                            searchOnType={true}
                            title="bygg.searchDocument.title"
                            placeholder="bygg.searchDocument.placeholder"
                        />
                    </Block>
                </Grid>
            </Grid>
            <Conditional
                condition={!error}
                errorComponent={
                    <ErrorNotification
                        title="byggDokument.errorDownload.title"
                        description="byggDokument.errorDownload.description"
                        backTarget={AppRoutes.mainPage()}
                        backTitle="general.goBackToFrontpage"
                        data-testid="ByggDokumentList"
                    />
                }
            >
                <Conditional
                    condition={documentTypes.length > 0}
                    errorComponent={
                        <ErrorNotification
                            title="byggDokument.errorNotFound.title"
                            description="byggDokument.errorNotFound.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            data-testid="ByggDokumentList"
                        />
                    }
                >
                    {documentTypes.map((documentType) => (
                        <Fragment key={documentType}>
                            <Block
                                data-type={`${documentType}`}
                                marginBottom="bulky"
                            >
                                <div
                                    id={documentType}
                                    className={classes.scrollTo}
                                />
                                <Heading
                                    klpHeadingLevel="THREE"
                                    marginVertical="tiny"
                                    translation={getTranslations(documentType)}
                                >
                                    {getTranslations(documentType)}
                                </Heading>
                                <Table
                                    id="ByggDokumentList"
                                    data-testid="ByggDokumentList"
                                    data={byggDokumentListGrouped[documentType]}
                                    columns={[
                                        new TableColumnDef({
                                            translation: 'byggDokument.etasje',
                                            key: 'etasje',
                                            type: 'string',
                                            sortable: true,
                                        }),
                                        new TableColumnDef({
                                            translation: 'byggDokument.navn',
                                            key: 'navn',
                                            type: 'string',
                                            sortable: true,
                                            align: 'left',
                                        }),
                                        new TableColumnDef({
                                            translation: 'byggDokument.nummer',
                                            key: 'nummer',
                                            type: 'string',
                                            sortable: true,
                                        }),
                                        new TableColumnDef({
                                            translation: 'byggDokument.revdato',
                                            key: 'revdato',
                                            type: 'date',
                                            sortable: true,
                                        }),
                                        new TableColumnDef({
                                            translation:
                                                'byggDokument.maalestokk',
                                            key: 'maalestokk',
                                            sortable: true,
                                        }),
                                        new TableColumnDef({
                                            translation:
                                                'general.previewDownload',
                                            key: 'id',
                                            render: renderDocumentPreviewDownload,
                                        }),
                                    ]}
                                />
                            </Block>
                        </Fragment>
                    ))}
                </Conditional>
            </Conditional>
        </Loader>
    );
};

ByggDokumentList.displayName = 'ByggDokumentList';
